import * as Sentry from '@sentry/nuxt';
import {useRuntimeConfig} from '#imports';

const runtimeConfig = useRuntimeConfig();

Sentry.init({
	enabled: !!runtimeConfig.public.sentry.enabled,
	dsn: runtimeConfig.public.sentry.dsn,
	environment: runtimeConfig.public.appEnv,
	integrations: [
		Sentry.extraErrorDataIntegration({depth: 3}),
		Sentry.breadcrumbsIntegration({
			dom: true,
			fetch: true,
			history: true,
			sentry: true,
			xhr: true,
		}),
	],
	sampleRate: 1,
	allowUrls: [
		// =====================================================================
		// ALLOWED ORIGINS (Whitelist)
		// Sentry will ONLY process errors if the failing script's URL matches
		// one of these Regex patterns. This automatically drops third-party
		// noise (Daktela, GTM, browser extensions).
		// =====================================================================

		// Production domain
		/globus\.cz/i,

		// Testing & Staging environments
		/globus-mujglobusweb\.globtest\.globenv\.cz/i,
		/globus-mujglobusweb\.globdev\.globenv\.cz/i,
		/globus-mujglobusweb\.dev-cache\.globenv\.cz/i,
		/globus-mujglobusweb\.dev\.globenv\.cz/i,

		// Local development
		/localhost/i,
	],
	ignoreErrors: [
		// =====================================================================
		// SAFE NOISE TO IGNORE: Errors with absolutely no diagnostic value
		// =====================================================================
		'AbortError',
		'The user aborted a request.',
		'Request aborted',
		'Request Timeout',
		'Storyblok client request was aborted',
		'ResizeObserver loop limit exceeded',
		'ResizeObserver loop completed with undelivered notifications.',
		'NS_ERROR_NOT_INITIALIZED',
	],
	beforeSend(event, hint) {
		const error = hint.originalException as any;
		const errorMessage = error?.message || String(error) || '';

		// 1. Fallback for AbortErrors (in case string matching fails)
		if (error && error.name === 'AbortError') {
			return null;
		}

		// 2. SMART NETWORK ERROR FILTERING
		const isNetworkError = errorMessage.includes('Network Error')
			|| errorMessage.includes('Load failed')
			|| errorMessage.includes('Failed to fetch')
			|| errorMessage.includes('NetworkError when attempting to fetch resource');

		if (isNetworkError) {
			const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

			if (isOffline) {
				// User lost connection -> Pure noise, safely discard
				return null;
			}

			// If the user is ONLINE, this likely indicates a fallen API (502/504), CORS failure,
			// or a strict AdBlocker blocking a critical endpoint.
			// Send to Sentry, but tag it for easier filtering and alerting.
			event.tags = {...event.tags, probable_cause: 'api_or_cors_down'};
		}

		// 3. TAG NUXT CHUNK LOAD ERRORS (Stale tabs after a new deployment)
		const isChunkLoadError = errorMessage.includes('Failed to fetch dynamically imported module')
			|| errorMessage.includes('Importing a module script failed');

		if (isChunkLoadError) {
			event.fingerprint = ['nuxt-chunk-load-error'];
		}

		// 4. BROWSER EXTENSION PROTECTION (AdBlockers, Translators, etc.)
		if (
			errorMessage.includes('chrome-extension://')
			|| errorMessage.includes('moz-extension://')
			|| errorMessage.includes('safari-extension://')
		) {
			return null;
		}

		return event;
	},
});
